import type { VodCategory } from '@setplex/tria-api'
import type { Store } from 'effector'
import type { VodWithPrice } from '~/shared/interfaces'
import type { FormattedVodWithPrice } from './index.h'

import { api } from '@setplex/tria-api'
import { combine, createEvent, sample } from 'effector'
import { model as i18n } from '~/core/i18n'
import { model as router } from '~/core/router'
import { ssePurchasesModel } from '~/features/payment/sse-purchases'
import { extendMediaItemByLabelPrice, resolveNewLabel } from '~/shared/tools'
import { formatVod } from './lib'

// *
// * events
// *

export const initCategoryPage = createEvent<{
  categoryId: number
}>()

export const initSubCategoryPage = createEvent<{
  categoryId: number
  subCategoryId: number
}>()

export const initSubCategoriesPage = createEvent<{
  categoryId: number
}>()

export const getVodsByCategoryId = createEvent<{
  page: number
  count?: number
  categoryId: number
}>()

export const seeAllMovieClick = createEvent<{ id: number }>()
sample({
  clock: seeAllMovieClick,
  fn: ({ id }) => `/content/movie/categories/${id}`,
  target: router.navigatePush,
})

export const seeAllMovieCategoryClick = createEvent<{ id: number }>()
sample({
  clock: seeAllMovieCategoryClick,
  fn: ({ id }) => `/content/movie/categories/${id}/subcategories`,
  target: router.navigatePush,
})

interface IRedirectToMoviePlayerParams {
  id?: number
  categoryId?: number
}

export const redirectToMoviePlayer = createEvent<IRedirectToMoviePlayerParams>()
sample({
  clock: redirectToMoviePlayer,
  fn: ({ id, categoryId }) =>
    `/player/movie?vodId=${id}&categoryId=${categoryId}`,
  target: router.navigatePush,
})

export interface IParentCategoryMoviesFormatted extends VodCategory {
  nameFormatted: string // 'Drama Genres'
}

export const $parentCategoryMoviesFormatted = combine(
  api.vods.$parentCategoryVods,
  i18n.$t,
  (content, { t }) => {
    const genresText = t('genre_other')

    return content
      ? {
          ...content,
          nameFormatted: `${content.name} ${genresText}`,
        }
      : null
  }
)

export const $formattedRecommendedVods: Store<VodWithPrice[]> = combine(
  api.vods.$realRecommendedVods,
  ssePurchasesModel.$vod,
  i18n.$t,
  (movies, adjust, { t }) =>
    movies?.map((movie) => {
      const formatted = extendMediaItemByLabelPrice(
        adjust(movie)
      ) as VodWithPrice

      return {
        ...formatted,
        newLabel: resolveNewLabel(formatted, t),
      }
    })
)

export const $recommendedVodsTitle: Store<string> = combine(i18n.$t, ({ t }) =>
  t('movies_may_like')
)

export const $lastAddedMoviesFormatted = combine(
  api.vods.$lastAddedMovies,
  ssePurchasesModel.$vod,
  (movies, adjust) =>
    movies.map((movie) => extendMediaItemByLabelPrice(adjust(movie)))
)

// *
// * CONNECTIONS
// *

sample({
  clock: getVodsByCategoryId,
  target: api.vods.getVodsByCategoryIdFx,
})

sample({
  clock: initCategoryPage,
  target: [api.vods.initVodsCategoryFx],
})

sample({
  clock: initSubCategoriesPage,
  target: [api.vods.initVodsSubCategoryFx],
})

sample({
  clock: initSubCategoryPage,
  fn: (clockData) => ({ categoryId: clockData.subCategoryId }),
  target: initCategoryPage,
})

export const $vodFormatted: Store<FormattedVodWithPrice | null> = combine(
  i18n.$t,
  ssePurchasesModel.$vod,
  api.vods.$vod,
  ({ t }, adjust, vod) => (vod ? formatVod({ item: vod, adjust, t }) : null)
)
